import { amorphicStatic, supertypeClass } from '@haventech/amorphic';
import { ClientPiiDataObj, DataElementS1, PiiConfigs, PiiController, PiiType, PiiUtils } from '@hit/pii-manager';
import { CoverpathPolicy } from '../../../coverpath-common/classes/coverpath_subclasses/CoverpathPolicy';
import { LoggedInUserInfo } from '../../../common/js/interfaces/CommonTypes';
import { Party } from '../../../common/js/customer/Party';
import { Application } from '../../../common/js/customer/Application';
import { Entity } from '../../../common/js/customer/Entity';
import { Person } from '../../../common/js/customer/Person';
import { LoggerSingleton, Category, PiiLevelEnum } from '../../../common/js/logger/LoggerSingleton';
import { dataElements as piiDataElements } from '../../../../config_pii_properties.json';
import { CasApplication } from '../../classes/coverpath_subclasses/CasApplication';

/**
 * This class can be used to provide security checks used within Coverpath.
 * Example use cases:
 * 1) check if an agent can esign a policy
 * 2) check if an agent or proxy can edit a policy
 * 3) check if an agent or proxy can view (not edit) a policy or application
 * 4) check if a coverpath client can edit or view a policy or application
 * 5) ......
 */
@supertypeClass({ toClient: false, toServer: true })
export class SecurityUtils {
  /**
   * @param agentProducerIds an array of producer ids
   * @param policy a Coverpath policy
   * @returns {boolean} true, if one of the agentProducerIds is primary agent id or secondary agent id of the policy
   * Otherwise, false
   */

  static canAccess(agentProducerIds: Array<string>, policy: CoverpathPolicy): boolean {
    const policyProducerIds: Array<string> = policy.allServicingAgents.map((agent) => agent.producerId);
    for (const producerId of agentProducerIds) {
      if (policyProducerIds.includes(producerId)) {
        return true;
      }
    }
    return false;
  }

  static canSign(producerId: string, policy: CoverpathPolicy): boolean {
    return policy.primaryPolicyAgent.agent.producerId === producerId;
  }

  static canAccessInsuredOwnerPii(agentProducerIds: Array<string>, policy: CoverpathPolicy, personId: string): boolean {
    if (!SecurityUtils.canAccess(agentProducerIds, policy)) {
      return false;
    } else {
      if (policy && !policy.ownerPerson && !policy.insured.person) {
        return false;
      }
      // if the policy's owner matches or policy's insured matches, then can access.
      if (
        (policy.ownerPerson && policy.ownerPerson._id === personId) ||
        (policy.insured.person && policy.insured.person._id === personId)
      ) {
        return true;
      }
      return false;
    }
  }

  static async setPiiByProperty(
    prop: string,
    attrValue: string,
    userInfo: LoggedInUserInfo,
    policy: CoverpathPolicy
  ): Promise<string> {
    SecurityUtils.setPiiConfig(piiDataElements);
    const shortProp = prop.replace('customer.application.primaryPolicy.', '');
    if (PiiConfigs.isPiiProperty(shortProp)) {
      // No need to call CoverpathUtils.updatePiiKeys here as this function is designed for CP application UI which uses Semotus.
      return PiiController.setPiiByPropertyS1(policy, shortProp, userInfo, attrValue);
    } else {
      LoggerSingleton.getInstance().warn(
        {
          module: 'SecurityUtils',
          function: 'setPiiByProperty',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
        },
        `Calling setPiiByProperty on a property (${prop}) that is not a valid PII property, policy id ${policy._id}, loggedInPrincipal party id: ${userInfo.userId}`
      );
      return attrValue;
    }
  }

  static async clonePiiByApplication(application: Application, loggedInUserInfo: LoggedInUserInfo): Promise<void> {
    SecurityUtils.setPiiConfig(piiDataElements);

    let clientPiiDataArray: ClientPiiDataObj[];
    try {
      clientPiiDataArray = await PiiController.clonePiiS1(application.primaryPolicy, loggedInUserInfo);

      LoggerSingleton.getInstance().info(
        {
          module: 'SecurityUtils',
          function: 'clonePiiByApplication',
          category: Category.security,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
        },
        `Successfully cloned primary policy PII for application number ${application.applicationNumber}`
      );
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'SecurityUtils',
          function: 'clonePiiByApplication',
          error: error,
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
        },
        'Error cloning primary policy PII by application'
      );
      return;
    }

    if (application.secondaryPolicy) {
      let clientPiiDataSecondaryPolicyArray: ClientPiiDataObj[];
      try {
        clientPiiDataSecondaryPolicyArray = await PiiController.clonePiiS1(
          application.secondaryPolicy,
          loggedInUserInfo
        );
        clientPiiDataArray.push(...clientPiiDataSecondaryPolicyArray);

        LoggerSingleton.getInstance().info(
          {
            module: 'SecurityUtils',
            function: 'clonePiiByApplication',
            category: Category.security,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
          },
          `Successfully cloned secondary policy PII for application number ${application.applicationNumber}`
        );
      } catch (err) {
        const error = {
          isHumanRelated: false,
          name: err.name,
          message: err.message,
          stack: err.stack,
        };
        LoggerSingleton.getInstance().error(
          {
            module: 'SecurityUtils',
            function: 'clonePiiByApplication',
            error: error,
            category: Category.security,
            context: {
              piiLevel: PiiLevelEnum.nonPII,
            },
          },
          'Error cloning secondary policy PII by application'
        );
        return;
      }
    }

    try {
      for (const clientPiiData of clientPiiDataArray) {
        await SecurityUtils.updatePiiKey(clientPiiData);
      }

      LoggerSingleton.getInstance().info(
        {
          module: 'SecurityUtils',
          function: 'clonePiiByApplication',
          category: Category.security,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
        },
        `Successfully updated PII keys for application number ${application.applicationNumber}`
      );
    } catch (err) {
      const error = {
        isHumanRelated: false,
        name: err.name,
        message: err.message,
        stack: err.stack,
      };
      LoggerSingleton.getInstance().error(
        {
          module: 'SecurityUtils',
          function: 'clonePiiByApplication',
          category: Category.security,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: error,
        },
        `Error updating PII keys for application number ${application.applicationNumber}`
      );
    }
  }

  static async setPiiByPersonId(
    personId: string,
    attrName: string,
    attrValue: string,
    userInfo: LoggedInUserInfo
  ): Promise<string> {
    SecurityUtils.setPiiConfig(piiDataElements);
    const piiKey: string = await PiiController.setPiiBySourceIdS1(personId, attrName, attrValue, userInfo);
    if (piiKey) {
      const clientPiiData: ClientPiiDataObj = {
        attrValue: piiKey,
        attrName: attrName,
        sourceId: personId,
      };
      await SecurityUtils.updatePiiKey(clientPiiData);
    }
    return piiKey;
  }

  static async getPiiValueByKey(
    attrKey: string,
    lastDigits: number = 0,
    includeHyphens: boolean = true,
    attrName: string = 'SSN'
  ): Promise<string> {
    if (attrKey) {
      SecurityUtils.setPiiConfig(piiDataElements);
      switch (attrName) {
        case 'SSN':
          return PiiController.getValueByKeySSN(attrKey, lastDigits, includeHyphens);
        default:
          return PiiController.getValueByKeyS1(attrKey, lastDigits);
      }
    } else {
      return attrKey;
    }
  }

  static async getPiiValuesByKeys(attrKeys: Set<string>, lastDigits: number = 0): Promise<Map<string, string>> {
    if (attrKeys && attrKeys.size > 0) {
      SecurityUtils.setPiiConfig(piiDataElements);
      return PiiController.getValuesByKeysS1(attrKeys, lastDigits);
    }
  }

  static async getPiiKeysByValue(
    attrValue: string,
    attrName: string,
    matchLast4Only: boolean = false
  ): Promise<Map<string, string>> {
    if (attrValue && attrName) {
      SecurityUtils.setPiiConfig(piiDataElements);
      return PiiController.getKeysByValueS1(attrValue, attrName, matchLast4Only);
    }
  }

  /*
  NOTE: Do not mark the hostObject dirty or let it persist to database when the hostObject is an instance of persistor object (such as CoverpathPolicy)
  This fillPiiData function is used to fill all the PII data associated with a given object.
  For example, when hostObject is a policy, this function replaces all the placeholder PII data with real PII data.
  Usage: In Radmin search window, when a policy is opened, a cp-data-svc API call is made and a policy is feteched through persistor fetch.
  After the fetch, this function is used to fill in the PII data in the policy object.
   */
  static async fillPiiData(hostObject: object): Promise<void> {
    if (!hostObject) {
      return;
    }

    SecurityUtils.setPiiConfig(piiDataElements);
    return PiiController.fillPiiData(hostObject);
  }

  static async getPiiByPersonId(personId: string, attrName: string, lastDigits: number = 0): Promise<string> {
    if (personId && attrName) {
      SecurityUtils.setPiiConfig(piiDataElements);
      return PiiController.getValueBySourceIdS1(personId, attrName, lastDigits);
    }
  }

  static async getPiiByPersonIds(
    personIds: Array<string>,
    attrName: string,
    lastDigits: number = 0
  ): Promise<Map<string, string>> {
    SecurityUtils.setPiiConfig(piiDataElements);
    if (personIds && personIds.length > 0 && attrName) {
      return PiiController.getValuesBySourceIdsS1(personIds, attrName, lastDigits);
    }
  }

  private static async updatePiiKey(clientPiiData: ClientPiiDataObj): Promise<void> {
    if (!clientPiiData || !clientPiiData.attrValue || !clientPiiData.attrName || !clientPiiData.sourceId) {
      throw new Error(`SecurityUtils.updatePiiKey: missing required data`);
    }
    const dataElementName: DataElementS1 = PiiType[clientPiiData.attrName].dbAttrName;
    const sourceTable = PiiConfigs.config[dataElementName].sourceTable;
    const sourceField = PiiConfigs.config[dataElementName].sourceField;
    await SecurityUtils.executeUpdatePiiKeyQuery(
      sourceTable,
      sourceField,
      clientPiiData.attrValue,
      clientPiiData.sourceId
    );
  }

  private static async executeUpdatePiiKeyQuery(
    sourceTable: string,
    sourceField: string,
    piiKey: string,
    sourceId: string
  ): Promise<void> {
    try {
      await Party.getKnex().client.raw(
        `update ${sourceTable} set "${sourceField}" = '${piiKey}' where _id = '${sourceId}';`
      );
    } catch (error) {
      LoggerSingleton.getInstance().error(
        {
          module: 'SecurityUtils',
          function: 'executeUpdatePiiKeyQuery',
          category: Category.request,
          context: {
            piiLevel: PiiLevelEnum.nonPII,
          },
          error: error,
        },
        `Error updating ${sourceTable} table (_id ${sourceId}) to use pii key ${piiKey}`
      );
      throw new Error(`Error updating ${sourceTable} table _id ${sourceId} to use pii key ${piiKey}`);
    }
  }

  static async isSSNSame(piiKey1: string, piiKey2: string): Promise<boolean> {
    const lastDigits = 0,
      includeHyphens = false;
    if (piiKey1 && piiKey2) {
      SecurityUtils.setPiiConfig(piiDataElements);
      return (
        (await SecurityUtils.getPiiValueByKey(piiKey1, lastDigits, includeHyphens, undefined)) ===
        (await SecurityUtils.getPiiValueByKey(piiKey2, lastDigits, includeHyphens, undefined))
      );
    }
    return false;
  }

  static isPiiKeyOnApplication(piiKey: string, application: CasApplication): boolean {
    return (
      SecurityUtils.isPiiKeyOnPolicy(piiKey, application.primaryPolicy) ||
      SecurityUtils.isPiiKeyOnPolicy(piiKey, application.secondaryPolicy)
    );
  }

  // Check parties on the policy to determine if the requested piiKey is
  // associated with the currently loaded application.
  private static isPiiKeyOnPolicy(piiKey: string, policy: CoverpathPolicy): boolean {
    if (!policy) {
      return false;
    }

    if (!PiiUtils.isPiiKey(piiKey)) {
      return true;
    }

    if (policy.insured.person?.SSN === piiKey) {
      return true;
    }

    if (policy.insured.signer && policy.insured.signer.SSN === piiKey) {
      return true;
    }

    if (policy.ownerPerson && policy.ownerPerson.SSN === piiKey) {
      return true;
    }

    if (policy.ownerEntity && policy.ownerEntity.authorizedSigner?.SSN === piiKey) {
      return true;
    }

    if (policy.contingentOwnerPerson && policy.contingentOwnerPerson.SSN === piiKey) {
      return true;
    }

    if (policy.otherOwner && policy.otherOwner.SSN === piiKey) {
      return true;
    }

    if (policy.payer && (policy.payer as Person)?.SSN === piiKey) {
      return true;
    }

    if (
      policy.payer &&
      (policy.payer as Entity)?.authorizedSigner &&
      (policy.payer as Entity).authorizedSigner.SSN === piiKey
    ) {
      return true;
    }

    for (const bene of policy.primaryBeneficiary) {
      if (bene.person?.SSN === piiKey) {
        return true;
      }
    }

    for (const conBen of policy.contingentBeneficiary) {
      if (conBen.person?.SSN === piiKey) {
        return true;
      }
    }

    if (policy.trustOrOtherEntity) {
      if (policy.trustOrOtherEntity.authorizedSigner && policy.trustOrOtherEntity.authorizedSigner.SSN === piiKey) {
        return true;
      }

      for (const trustee of policy.trustOrOtherEntity.authorizedTrusteeList) {
        if (trustee.trusteePerson && trustee.trusteePerson.SSN === piiKey) {
          return true;
        }

        if (trustee.entityAuthorizedSigner && trustee.entityAuthorizedSigner.SSN === piiKey) {
          return true;
        }
      }

      for (const grantor of policy.trustOrOtherEntity.trustGrantorList) {
        if (grantor.grantorPerson && grantor.grantorPerson.SSN === piiKey) {
          return true;
        }
      }
    }

    if (policy.insured.person?.idNumber === piiKey) {
      return true;
    }

    return false;
  }

  static setPiiConfig(piiDataElements) {
    if (!PiiConfigs.config) {
      PiiConfigs.loadConfigFromJson(piiDataElements);
    }
  }
}
